import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { ArrowUpRight } from "lucide-react";
import { Badge, Button, Dialog, DialogActions, FilterBar, Select, Table, SearchField } from "@studio";
import { api, errorMessage } from "../lib/api";
import { can } from "../lib/access";
import { useAuth } from "../lib/auth";
import { duration, errorKindNames, fmtDateTime, fmtTime, num, publishLabel, runKindNames, runLabel, taskLabel, taskTypeNames } from "../lib/format";
import { keys, useAction, useCustomers, useRun, useTask, useTaskEvents } from "../lib/queries";
import type { RunRow, TaskEvent } from "../lib/types";
import { Empty, ErrorBox, InfoRow, Loading, Pager, Panel, StatusBadge } from "../console/ui";
import { failureLabels, PublicationEvidence, publicationFromTask } from "./review";
import { ApprovalPanel, PublishTimeline, publishResultText, ThreadPanel } from "./publish";
import { CreateTaskDialog } from "./create-task";
import { RetryRunDialog } from "./retry-run";
import { PublishDetailDialog } from "./customer-publish";
import { defaultRunTab, isActiveRun, RunDetailTabs, type RunTab } from "./run-detail";
import { ReadableDetails } from "@studio-features/meta-crew/readable-details";
import { errorDetailView } from "../console/readable";
import { urlParam } from "../console/metrics";

/** 진행 중(대기·실행·머신 대기) 상태 필터 값. 칩과 상태 선택이 같은 값을 쓴다. */
export const ACTIVE_FILTER = "queued,running,waiting_machine";

export const RUN_STATUS_FILTERS: [string, string][] = [
  ["", "전체"],
  [ACTIVE_FILTER, "진행·대기"],
  ["failed,dead", "실패"],
  ["held", "보류"],
  ["succeeded", "성공"],
  ["cancelled", "취소"],
  // 시안 6a084bb: 게시 작업의 실패 분류 4종 (작업 errorKind 로 거른다).
  ...Object.entries(failureLabels).map(([k, v]): [string, string] => [`ek:${k}`, v]),
];

/** 고객 웹(발행 요청만 보는 회원)의 상태 필터. */
export const PUBLISH_STATUS_FILTERS: [string, string][] = [
  ["", "전체"],
  ["held", "확인 중 · 수정 요청 · 발행 대기"],
  ["queued,running,waiting_machine", "발행 중"],
  ["succeeded", "발행됨"],
  ["failed,dead", "발행 실패"],
  ["cancelled", "취소 · 반려"],
];

/** 상태 필터 값 → 실행 목록 API 파라미터 (ek: 접두는 작업 실패 분류). */
export function runFilterParams(value: string): { status?: string; errorKind?: string } {
  if (value.startsWith("ek:")) return { status: "failed,dead", errorKind: value.slice(3) };
  return value ? { status: value } : {};
}

const PERIODS: [string, string, number][] = [
  ["", "전체 기간", 0],
  ["1h", "최근 1시간", 3600_000],
  ["24h", "최근 24시간", 86_400_000],
  ["7d", "최근 7일", 7 * 86_400_000],
  ["30d", "최근 30일", 30 * 86_400_000],
];

/**
 * 시안 RunList 의 추가 필터 (작업 종류 · 오류 분류 · 기간). 서버 Run 목록 API 의 kind·errorKind·from 으로 거른다.
 * 상태 필터의 "ek:" 실패 분류와 겹치면 이 오류 분류가 우선한다.
 */
/** 작업 범위 (2026-10-21 사용자): 관리자 목록은 "관리자 등록 작업"이 기본, 시스템(스케줄러)·고객 발행·전체는 필요할 때 고른다. */
const RUN_SCOPES: Record<string, { label: string; params: { kind?: string; origin?: string } }> = {
  staff: { label: "관리자 등록 작업", params: { kind: "machine_task", origin: "staff" } },
  customer: { label: "고객 발행 요청", params: { kind: "machine_task", origin: "customer" } },
  system: { label: "시스템 작업 (예약·스케줄러)", params: { kind: "system" } },
  all: { label: "전체", params: {} },
};

export function useRunExtraFilters(onChange?: () => void): { params: { kind?: string; origin?: string; errorKind?: string; from?: string }; node: ReactNode } {
  // 관리자는 "관리자 등록 작업"부터 본다. 고객 회원은 원래 자기 발행 요청만 보이므로 범위를 고르지 않는다.
  const staff = !!useAuth((s) => s.me)?.isStaff;
  // KPI 카드에서 오면 ?scope= (staff·customer·system·all) · ?period= (1h·24h·7d·30d) 로 첫 필터를 잡는다
  const [kind, setKind] = useState(() => {
    const v = urlParam("scope");
    return staff && v && v in RUN_SCOPES ? v : staff ? "staff" : "all";
  });
  const [errorKind, setErrorKind] = useState("");
  const [period, setPeriod] = useState(() => {
    const v = urlParam("period");
    return v && PERIODS.some((p) => p[0] === v) ? v : "";
  });
  const ms = PERIODS.find((p) => p[0] === period)?.[2] ?? 0;
  // 기간 시작 시각은 선택할 때 한 번 정한다 (매 렌더마다 바뀌면 쿼리 키가 계속 달라진다).
  const [from, setFrom] = useState<string | undefined>(undefined);
  useEffect(() => setFrom(ms ? new Date(Date.now() - ms).toISOString() : undefined), [ms]);
  const set = (f: (v: string) => void) => (e: { target: { value: string } }) => {
    f(e.target.value);
    onChange?.();
  };
  return {
    params: { ...(RUN_SCOPES[kind]?.params ?? {}), ...(errorKind ? { errorKind } : {}), ...(from ? { from } : {}) },
    node: (
      <>
        {staff && (
          <Select label="작업 범위" value={kind} onChange={set(setKind)}>
            {Object.entries(RUN_SCOPES).map(([k, v]) => (
              <option key={k} value={k}>
                {v.label}
              </option>
            ))}
          </Select>
        )}
        <Select label="오류 분류" value={errorKind} onChange={set(setErrorKind)}>
          <option value="">전체</option>
          {Object.entries({ ...errorKindNames, ...failureLabels }).map(([k, v]) => (
            <option key={k} value={k}>
              {v}
            </option>
          ))}
        </Select>
        <Select label="기간" value={period} onChange={set(setPeriod)}>
          {PERIODS.map(([k, l]) => (
            <option key={k} value={k}>
              {l}
            </option>
          ))}
        </Select>
      </>
    ),
  };
}

/** executor "machine:<publicId>" → 머신 이름. 목록에는 머신 publicId 만 오므로 머신 목록으로 이름을 찾는다. */
function machineLabelOf(r: RunRow, machines?: { id: string; label: string }[]): string | null {
  const id = r.executor?.startsWith("machine:") ? r.executor.slice(8) : null;
  if (!id) return null;
  return machines?.find((m) => m.id === id)?.label ?? "머신";
}

/**
 * 실행 이력 표 (시안 console-panels RunList): 작업 검색 + 실행 상태, 열은 작업/실행 번호 · 유형 · 대상 고객사 · 상태 · 실행 위치(관리자: 실행 위치/고객사) · 시작/시도.
 * customerMode(고객 웹)면 고객용 명칭·열: 발행 · 상태 · 일시 · 결과. 목록·필터 상태는 부모가 가진다.
 */
export function RunTable({
  rows,
  total,
  offset,
  limit,
  onOffset,
  status,
  onStatus,
  admin = false,
  customerMode = false,
  loading,
  error,
  onRetry,
  onOpen,
  customers,
  customerId,
  onCustomer,
  extraFilters,
  machines,
  activeCount,
}: {
  rows: RunRow[];
  total: number;
  offset: number;
  limit: number;
  onOffset: (o: number) => void;
  status: string;
  onStatus: (s: string) => void;
  admin?: boolean;
  customerMode?: boolean;
  loading: boolean;
  error: string | null;
  onRetry: () => void;
  onOpen: (r: RunRow) => void;
  customers?: { id: string; name: string }[];
  customerId?: string;
  onCustomer?: (id: string) => void;
  extraFilters?: ReactNode;
  /** 실행 위치 열에 쓸 머신 이름 (관리자: 전체 머신, 고객사: 그 고객사 머신). */
  machines?: { id: string; label: string }[];
  /** "진행 중" 칩 옆 숫자 (관리자: 진행 중 작업 API). 없으면 숫자를 빼고 보인다. */
  activeCount?: number;
}) {
  const [query, setQuery] = useState("");
  const filters = customerMode ? PUBLISH_STATUS_FILTERS : RUN_STATUS_FILTERS;
  const q = query.trim().toLowerCase();
  const visible = q ? rows.filter((r) => `${jobTitle(r.jobName)} ${r.id} ${r.taskId ?? ""}`.toLowerCase().includes(q)) : rows;
  const scheduleText = (r: RunRow) => (r.attempt > 1 ? `${r.attempt}번째 시도` : r.kind === "system" ? "시스템" : "일회성");
  return (
    <>
      <FilterBar>
        <SearchField label="작업 검색" showLabel placeholder={customerMode ? "글 제목" : "작업 이름 또는 실행 번호"} value={query} onChange={(e) => setQuery(e.target.value)} />
        <Select label={customerMode ? "발행 상태" : "실행 상태"} value={status} onChange={(e) => onStatus(e.target.value)}>
          {filters.map(([v, l]) => (
            <option key={v} value={v}>
              {l}
            </option>
          ))}
        </Select>
        {admin && customers && onCustomer && (
          <Select label="고객사" value={customerId ?? ""} onChange={(e) => onCustomer(e.target.value)}>
            <option value="">전체 고객사</option>
            {customers.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </Select>
        )}
        {extraFilters}
      </FilterBar>
      {!customerMode && (
        // 시안 c9ee685 RunList: 전체 / 진행 중 칩. 진행 중 행은 위로 올리고 점으로 표시한다.
        <div className="lwe-run-filter">
          <Button size="sm" variant="secondary" aria-pressed={status !== ACTIVE_FILTER} onClick={() => onStatus("")}>
            전체 실행
          </Button>
          <Button size="sm" variant="secondary" aria-pressed={status === ACTIVE_FILTER} onClick={() => onStatus(status === ACTIVE_FILTER ? "" : ACTIVE_FILTER)}>
            <span className="lwe-live-dot" />
            진행 중{activeCount !== undefined ? ` ${activeCount}` : ""}
          </Button>
        </div>
      )}
      {error ? (
        <ErrorBox error={error} onRetry={onRetry} />
      ) : loading ? (
        <Loading />
      ) : visible.length === 0 ? (
        <Empty>{customerMode ? (rows.length ? "검색어에 맞는 발행이 없습니다." : "아직 발행한 글이 없습니다. 새 발행으로 첫 글을 보내 보세요.") : "조건에 맞는 실행 이력이 없습니다."}</Empty>
      ) : customerMode ? (
        <Table>
          <thead>
            <tr>
              <th>발행 / 번호</th>
              <th>상태</th>
              <th>일시</th>
              <th>결과</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {visible.map((r) => (
              <tr key={r.id}>
                <td>
                  <button className="meta-crew-text-button" onClick={() => onOpen(r)}>
                    {jobTitle(r.jobName)}
                  </button>
                  <small className="meta-crew-sub">
                    {r.id.slice(0, 8)} · v{r.revision ?? 1}
                    {r.unreadForCustomer ? (
                      <>
                        {" · "}
                        <Badge tone="coral">새 답글 {r.unreadForCustomer}</Badge>
                      </>
                    ) : null}
                  </small>
                </td>
                <td>
                  <StatusBadge label={publishLabel(r)} />
                </td>
                <td>
                  {r.status === "succeeded" ? fmtTime(r.finishedAt) : r.jobRunAt && new Date(r.jobRunAt).getTime() > Date.now() ? `예약 ${fmtTime(r.jobRunAt)}` : fmtTime(r.startedAt ?? r.createdAt)}
                  <small className="meta-crew-sub">{r.status === "succeeded" ? "게시 시각" : "접수 " + fmtTime(r.createdAt)}</small>
                </td>
                <td>
                  <span className="meta-crew-sub">{(r.status === "succeeded" ? (r.resultSummary ?? "발행됨") : (r.errorMessage ?? "")).slice(0, 70) || "-"}</span>
                </td>
                <td>
                  <Button size="sm" variant="ghost" onClick={() => onOpen(r)}>
                    상세 <ArrowUpRight size={13} />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      ) : (
        <Table>
          <thead>
            <tr>
              <th>작업 / 실행 번호</th>
              <th>유형</th>
              <th>대상 고객사</th>
              <th>상태</th>
              <th>{admin ? "실행 위치 / 고객사" : "실행 위치"}</th>
              <th>시작 / 시도</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {[...visible]
              .sort((a, b) => Number(isActiveRun(b)) - Number(isActiveRun(a)))
              .map((r) => {
              const where = machineLabelOf(r, machines);
              return (
                <tr key={r.id}>
                  <td>
                    <button className="meta-crew-text-button" onClick={() => onOpen(r)}>
                      {jobTitle(r.jobName)}
                    </button>
                    <small className="meta-crew-sub">
                      {r.id.slice(0, 8)} · {taskTypeNames[r.taskType ?? ""] ?? (r.kind === "system" ? "시스템" : "작업")} · {scheduleText(r)}
                      {r.origin === "customer" && (
                        <>
                          {" "}
                          <Badge tone="amber">고객 요청{r.revision && r.revision > 1 ? ` v${r.revision}` : ""}</Badge>
                        </>
                      )}
                      {r.unreadForStaff ? (
                        <>
                          {" "}
                          <Badge tone="coral">새 답글 {r.unreadForStaff}</Badge>
                        </>
                      ) : null}
                    </small>
                  </td>
                  <td>{runKindNames[r.kind] ?? r.kind}</td>
                  <td>
                    {r.kind === "machine_task" ? (r.targetCustomerName ?? r.customerName ?? "—") : "—"}
                    {r.crew && (
                      <small className="meta-crew-sub">
                        <Badge tone="blue">크루 작업</Badge>
                      </small>
                    )}
                  </td>
                  <td>
                    {isActiveRun(r) && <span className="lwe-live-dot" aria-label="진행 중" />} <StatusBadge label={runLabel(r.status)} />
                    {(r.taskErrorKind || r.errorKind) && <small className="meta-crew-sub">{failureLabels[r.taskErrorKind ?? ""] ?? errorKindNames[r.taskErrorKind ?? r.errorKind ?? ""] ?? r.errorKind}</small>}
                  </td>
                  <td>
                    {where ?? (r.kind === "system" ? "코어" : r.status === "queued" || r.status === "held" ? "배정 전" : "—")}
                    {admin && <small className="meta-crew-sub">{r.customerName ?? "시스템"}</small>}
                  </td>
                  <td>
                    {fmtTime(r.startedAt ?? r.scheduledAt ?? r.createdAt)}
                    <small className="meta-crew-sub">{r.attempt ? `${r.attempt}번째 시도` : "실행 전"}</small>
                  </td>
                  <td>
                    <Button size="sm" variant="ghost" onClick={() => onOpen(r)}>
                      상세 <ArrowUpRight size={13} />
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      )}
      <Pager offset={offset} limit={limit} total={total} onChange={onOffset} />
    </>
  );
}

/**
 * 진행 로그: 목록 안에서만 스크롤한다. 맨 아래를 보고 있으면 새 줄을 따라 내려가고,
 * 위로 올려 읽는 중이면 멈춘 채 "새 로그 N줄" 버튼으로 돌아온다.
 */
function EventLog({ items, live }: { items: TaskEvent[]; live: boolean }) {
  const box = useRef<HTMLOListElement>(null);
  const [follow, setFollow] = useState(true);
  const [seen, setSeen] = useState(0);
  const last = items.at(-1)?.seq ?? 0;
  const toBottom = () => {
    const el = box.current;
    if (el) el.scrollTop = el.scrollHeight;
  };
  useLayoutEffect(() => {
    if (follow) {
      toBottom();
      setSeen(items.length);
    }
  }, [last, follow, items.length]);
  const onScroll = () => {
    const el = box.current;
    if (el) setFollow(el.scrollHeight - el.scrollTop - el.clientHeight < 24);
  };
  const fresh = follow ? 0 : items.length - seen;
  return (
    <div className="mcc-events-wrap">
      <ol className="mcc-events is-scroll" ref={box} onScroll={onScroll} aria-live={live ? "polite" : undefined}>
        {items.map((e) => (
          <li key={e.seq}>
            <time>{fmtTime(e.at)}</time>
            <span className={e.source === "server" ? "muted" : ""}>{e.text || e.kind}</span>
          </li>
        ))}
      </ol>
      {fresh > 0 && (
        <button type="button" className="mcc-events-jump" onClick={() => setFollow(true)}>
          새 로그 {fresh}줄 · 맨 아래로
        </button>
      )}
    </div>
  );
}

const ENV_FAILURES =new Set(["device_unavailable", "worker_lost", "needs_human", "login_required", "llm_limit", "pre_step_failed"]);

/**
 * 실행 상세 (시안 RunDetail): Run 타임라인 + 작업 결과 + 진행 로그 + 재시도/취소.
 * 고객 발행 요청(origin=customer)이면 직원에게는 승인 패널과 스레드, 고객에게는 발행 타임라인·원고·스레드·고쳐 쓰기.
 */
export function RunDetailDialog({
  runId,
  customerId,
  onClose,
  onReview,
  onBack,
  initialTab,
  hidden = false,
  onMachine,
}: {
  runId: string | null;
  customerId: string | null;
  /** 닫기. 진행 중이던 실행이면 active=true (부모가 "작업은 계속 진행됩니다" 알림). */
  onClose: (active?: boolean) => void;
  onReview?: (taskId: string) => void;
  onBack?: () => void;
  /** 직원 탭 진입 의도 (없으면 상태로 정함, run-detail.tsx defaultRunTab). */
  initialTab?: RunTab;
  /** 머신 상세로 바꿔 보여 주는 동안 숨김 (다이얼로그는 한 단, 요청서 7-3). */
  hidden?: boolean;
  onMachine?: (m: { machineId: string; customerId: string; label?: string | null }) => void;
}) {
  const me = useAuth((s) => s.me);
  // 고객 발행 요청(origin=customer): 고객 회원은 시안 PublishDetail, 직원은 같은 실행 상세의 "대화" 탭 (시안 c9ee685).
  const staff = !!me?.isStaff;
  const [tab, setTab] = useState<RunTab | null>(null);
  const customers = useCustomers("", staff);
  useEffect(() => setTab(null), [runId]);
  const run = useRun(customerId, runId);
  const effectiveCustomer = customerId ?? run.data?.customerId ?? null;
  const taskId = run.data?.taskId ?? null;
  const task = useTask(effectiveCustomer ?? "", effectiveCustomer ? taskId : null);
  const events = useTaskEvents(effectiveCustomer ?? "", effectiveCustomer && !staff && can(me, "TASK_DETAIL", "CONSOLE_READ", "PUBLISH_READ") ? taskId : null);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [edit, setEdit] = useState<"resubmit" | "republish" | null>(null);
  // 관리자 재시도는 설정(AI 모델·머신·폰)을 바꿀 수 있는 창을 거친다 (retry-run.tsx)
  const [retrying, setRetrying] = useState(false);
  useEffect(() => setRetrying(false), [runId]);
  const base = customerId ? `/c/${customerId}/runs/${runId}` : `/admin/runs/${runId}`;
  const invalidate = [keys.run(customerId ?? "admin", runId ?? ""), ["c"], ["admin", "runs"], ["admin", "overview"]];
  const retry = useAction(() => api.post(`${base}/retry`), invalidate);
  const cancelRun = useAction(() => api.post(`${base}/cancel`), invalidate);
  const cancelTask = useAction(() => api.post(`/c/${effectiveCustomer}/tasks/${taskId}/cancel`, { reason: "콘솔에서 취소" }), invalidate);

  const r = run.data;
  const t = task.data;
  const publish = r?.origin === "customer";
  const awaiting = !!r && r.status === "held" && (r.errorKind === "approval_required" || r.errorKind === "changes_requested");
  // 게시 작업(5단계)은 결과 증거를 보여주고, 검토 반려·결과 불명 등은 자동 재시도하지 않는다.
  const customerName = (r as { customerName?: string | null } | undefined)?.customerName ?? customers.data?.items.find((c) => c.id === effectiveCustomer)?.name ?? "실행 고객사";
  const pub = t && effectiveCustomer && staff ? publicationFromTask(t, { customerId: effectiveCustomer, name: customerName }) : null;
  const failKind = t?.errorKind ?? r?.errorKind ?? "";
  const canRetry =
    !!r && ["failed", "dead", "cancelled"].includes(r.status) && r.kind === "machine_task" && !pub?.failure && (staff ? can(me, "TASK_CREATE") : can(me, "PUBLISH_WRITE") && ENV_FAILURES.has(failKind));
  const canCancelRun = !!r && ["queued", "held", "waiting_machine"].includes(r.status) && (staff ? can(me, "TASK_CANCEL") : can(me, "PUBLISH_WRITE") && ["queued", "held"].includes(r.status));
  const canCancelTask = !!t && can(me, "TASK_CANCEL", "TASK_CANCEL_LOCAL") && ["queued", "assigned", "running"].includes(t.status);
  const canResubmit = !!r && awaiting && can(me, "PUBLISH_WRITE", "TASK_CREATE") && !!r.manuscript;
  const canRepublish = !!r && publish && !!r.manuscript && can(me, "PUBLISH_WRITE") && (["cancelled", "failed", "dead"].includes(r.status) || r.reviewStatus === "deleted");
  const act = (m: { mutateAsync: () => Promise<unknown> }) => () => {
    setError(null);
    m.mutateAsync().catch((e) => setError(errorMessage(e)));
  };
  const usage = t?.result?.usage as Record<string, number | string> | undefined;
  if (runId && r && publish && effectiveCustomer && !staff) {
    return <PublishDetailDialog key={runId} customerId={effectiveCustomer} runId={runId} staff={false} onClose={() => onClose()} onBack={onBack} />;
  }
  const live = !!r && isActiveRun(r, t);
  const close = () => onClose(live);
  const primary = (() => {
    if (pub?.resultReview && onReview && t) return { label: "결과 검토함 열기", onClick: () => onReview(t.id) };
    if (canResubmit && !staff) return { label: "원고 고쳐 다시 보내기", onClick: () => setEdit("resubmit") };
    if (canCancelTask) return { label: "작업 취소", tone: "danger" as const, onClick: act(cancelTask), disabled: cancelTask.isPending };
    if (canCancelRun) return { label: staff ? "실행 취소" : "발행 취소", tone: "danger" as const, onClick: act(cancelRun), disabled: cancelRun.isPending };
    if (canRetry) return staff ? { label: "재시도…", onClick: () => setRetrying(true) } : { label: "다시 시도", onClick: act(retry), disabled: retry.isPending };
    if (canRepublish) return { label: "이 원고로 다시 발행", onClick: () => setEdit("republish") };
    return undefined;
  })();

  if (staff && retrying && r && effectiveCustomer && !hidden) {
    return (
      <RetryRunDialog
        base={base}
        run={r}
        task={t}
        customerId={effectiveCustomer}
        customerName={customerName}
        typeName={(t && taskTypeNames[t.type]) ?? jobTitle(r.jobName)}
        invalidate={invalidate}
        onClose={() => setRetrying(false)}
        onDone={() => {
          setRetrying(false);
          setNote("재시도를 보냈습니다. 새 시도는 실행 목록에 따로 생깁니다.");
        }}
      />
    );
  }
  if (staff) {
    // 직원: 고정 요약 + 7탭 (시안 c9ee685 RunDetail). 본문만 탭 안에서 스크롤한다.
    const footNote = error ?? note ?? (pub?.failure === "review_rejected" ? "원고를 고쳐 새 작업으로 등록하세요. 자동 재시도하지 않습니다." : can(me, "TASK_CREATE", "TASK_CANCEL") ? "요청 결과는 중앙 처리 후 반영됩니다." : "조회 전용");
    return (
      <Dialog
        open={!!runId && !hidden}
        onOpenChange={(v) => !v && close()}
        title={t?.title ?? (r ? jobTitle(r.jobName) : "실행 상세")}
        description={r ? `${runKindNames[r.kind] ?? r.kind} · ${r.attempt}번째 시도 · ${r.id.slice(0, 8)}${publish ? ` · 고객 요청 v${r.revision ?? 1}` : ""}` : ""}
        size="detail"
        contentPadding={r ? "none" : "default"}
        footer={<DialogActions secondary={{ label: onBack ? "← 목록으로" : "닫기", onClick: onBack ?? close }} primary={primary} note={footNote} />}
      >
        {run.isLoading ? (
          <Loading />
        ) : run.error ? (
          <ErrorBox error={errorMessage(run.error)} />
        ) : r ? (
          <RunDetailTabs
            run={r}
            task={t}
            customerId={effectiveCustomer}
            pub={pub}
            customerName={customerName}
            tab={tab ?? initialTab ?? defaultRunTab(r, t, pub)}
            onTab={setTab}
            hidden={hidden}
            unread={r.unreadForStaff ?? 0}
            onMachine={onMachine && t?.machineId && effectiveCustomer ? () => onMachine({ machineId: t.machineId!, customerId: effectiveCustomer, label: t.machineLabel }) : undefined}
            info={
              <>
                {r.manuscript && (
                  <Panel title={`원고 · v${r.revision ?? 1}${r.attachmentCount ? ` · 첨부 ${r.attachmentCount}개` : ""}`}>
                    <pre className="lwe-manuscript">{r.manuscript}</pre>
                  </Panel>
                )}
                <Panel title="SEO 키워드">
                  <div className="lwd-panel-body">{(r.keywords ?? t?.keywords ?? []).join(" · ") || "지정된 키워드 없음"}</div>
                </Panel>
              </>
            }
            conversation={
              publish && effectiveCustomer ? (
                <div className="lwd-stack">
                  {awaiting && (
                    <ApprovalPanel
                      base={base}
                      run={r}
                      visibility={(t?.params?.post as { visibility?: string } | undefined)?.visibility ?? manuscriptVisibility(r.manuscript)}
                      invalidate={invalidate}
                      onDone={(m) => {
                        setError(null);
                        setNote(m);
                      }}
                    />
                  )}
                  {!awaiting && <PublishTimeline run={r} task={t} />}
                  {r.jobId && <ThreadPanel customerId={effectiveCustomer} jobId={r.jobId} canWrite={can(me, "PUBLISH_WRITE", "TASK_DETAIL")} />}
                </div>
              ) : null
            }
          />
        ) : null}
      </Dialog>
    );
  }

  return (
    <Dialog
      open={!!runId}
      onOpenChange={(v) => !v && onClose()}
      title={t?.title ?? (r ? jobTitle(r.jobName) : "실행 상세")}
      description={r ? (staff ? `${runKindNames[r.kind] ?? r.kind} · ${r.attempt}번째 시도 · ${r.id.slice(0, 8)}${publish ? ` · 고객 요청 v${r.revision ?? 1}` : ""}` : `접수 ${fmtDateTime(r.createdAt)} · 원고 v${r.revision ?? 1}`) : ""}
      size="detail"
      footer={<DialogActions secondary={{ label: "닫기", onClick: () => onClose() }} primary={primary} note={error ?? note ?? (pub?.failure === "review_rejected" ? "원고를 고쳐 새 작업으로 등록하세요. 자동 재시도하지 않습니다." : undefined)} />}
    >
      {run.isLoading ? (
        <Loading />
      ) : run.error ? (
        <ErrorBox error={errorMessage(run.error)} />
      ) : r ? (
        <div className="lwd-stack">
          {publish && !staff ? (
            <>
              <div className="mcc-detail-top">
                <StatusBadge label={publishLabel({ ...r, taskStatus: t?.status, stage: t?.result?.stage ?? r.stage })} />
                {r.jobRunAt && new Date(r.jobRunAt).getTime() > Date.now() && <span>예약 {fmtDateTime(r.jobRunAt)}</span>}
              </div>
              <PublishTimeline run={r} task={t} />
              {publishResultText(r, t) && <p className="mcc-description">{publishResultText(r, t)}</p>}
            </>
          ) : (
            <>
              <div className="mcc-detail-top">
                <StatusBadge label={runLabel(r.status)} />
                {t && <StatusBadge label={taskLabel(t.status)} />}
                {publish && <StatusBadge label={publishLabel({ ...r, taskStatus: t?.status, stage: t?.result?.stage ?? r.stage })} />}
                <span>
                  {t?.assignedDeviceSerial ? `폰 ${t.assignedDeviceSerial}` : (t?.machineLabel ?? "")} {t?.startedAt ? `· ${duration(t.startedAt, t.finishedAt)}` : ""}
                </span>
              </div>
              {publish && <PublishTimeline run={r} task={t} />}
              {pub ? (
                <PublicationEvidence p={pub} />
              ) : (
                (t?.result?.summary || r.resultSummary || r.errorMessage || t?.error) && <p className="mcc-description">{t?.result?.summary ?? r.resultSummary ?? t?.error ?? r.errorMessage}</p>
              )}
            </>
          )}
          {publish && staff && awaiting && effectiveCustomer && (
            <ApprovalPanel
              base={base}
              run={r}
              visibility={(t?.params?.post as { visibility?: string } | undefined)?.visibility ?? manuscriptVisibility(r.manuscript)}
              invalidate={invalidate}
              onDone={(m) => {
                setError(null);
                setNote(m);
              }}
            />
          )}
          {publish && r.manuscript && (
            <Panel title={`원고 · v${r.revision ?? 1}${r.attachmentCount ? ` · 첨부 ${r.attachmentCount}개` : ""}`}>
              <details className="mcc-details" open={!staff}>
                <summary>원고 보기</summary>
                <pre className="mcc-pre mcc-manuscript-pre">{r.manuscript}</pre>
              </details>
            </Panel>
          )}
          {!!(r.keywords ?? t?.keywords)?.length && staff && (
            <Panel title="SEO 키워드">
              <div className="seo-pad">
                <div className="seo-chips">
                  {(r.keywords ?? t?.keywords ?? []).map((k) => (
                    <span key={k}>{k}</span>
                  ))}
                </div>
                <p className="seo-caption">요청 시점부터 이 고객사의 분석 대상으로 추적합니다.</p>
              </div>
            </Panel>
          )}
          {t && staff && (
            <Panel title="작업 정보">
              <dl className="mcc-kv">
                <dt>유형</dt>
                <dd>{taskTypeNames[t.type] ?? t.type}</dd>
                <dt>등록</dt>
                <dd>{fmtDateTime(t.createdAt)}</dd>
                <dt>시작 / 끝</dt>
                <dd>
                  {fmtDateTime(t.startedAt)} / {fmtDateTime(t.finishedAt)}
                </dd>
                {t.errorKind && (
                  <>
                    <dt>실패 분류</dt>
                    <dd>{errorKindNames[t.errorKind] ?? t.errorKind}</dd>
                  </>
                )}
                {usage && (
                  <>
                    <dt>AI 사용</dt>
                    <dd>
                      {String(usage.model ?? "")} · 입력 {num(Number(usage.inputTokens ?? 0))} · 캐시 {num(Number(usage.cachedInputTokens ?? 0))} · 출력{" "}
                      {num(Number(usage.outputTokens ?? 0))} 토큰
                    </dd>
                  </>
                )}
              </dl>
              {t.instructions && (
                <details className="mcc-details">
                  <summary>작업 지시</summary>
                  <pre className="mcc-pre">{t.instructions}</pre>
                </details>
              )}
            </Panel>
          )}
          {publish && effectiveCustomer && r.jobId && <ThreadPanel customerId={effectiveCustomer} jobId={r.jobId} canWrite={can(me, "PUBLISH_WRITE", "TASK_DETAIL")} />}
          {events.data && events.data.items.length > 0 && (
            <Panel title={staff ? `진행 로그 · ${events.data.items.length}` : "진행 기록"}>
              <EventLog items={events.data.items.slice(-200)} live={!!t && ["queued", "assigned", "running"].includes(t.status)} />
            </Panel>
          )}
          {staff && (
            <Panel title="실행 기록">
              {r.events.map((e, i) => (
                <InfoRow key={i} title={`${fmtTime(e.at)} · ${e.kind}`} description={e.message ?? ""} />
              ))}
              {r.events.length === 0 && <InfoRow title="기록 없음" description="아직 실행 기록이 없습니다." />}
              {(r.errorDetail != null || r.errorMessage) && (
                <ReadableDetails view={errorDetailView({ errorKind: r.errorKind, error: r.errorMessage, errorDetail: r.errorDetail }, staff)} canReadDebug={staff} />
              )}
            </Panel>
          )}
          {(canResubmit || canRepublish) && staff === false && primary?.label !== "원고 고쳐 다시 보내기" && canResubmit && (
            <div className="lwd-actions">
              <Button variant="secondary" onClick={() => setEdit("resubmit")}>
                원고 고쳐 다시 보내기
              </Button>
            </div>
          )}
        </div>
      ) : null}
      {effectiveCustomer && r && (
        <CreateTaskDialog
          customerId={effectiveCustomer}
          open={edit !== null}
          onClose={() => setEdit(null)}
          initialManuscript={r.manuscript ?? ""}
          resubmitRunId={edit === "resubmit" ? r.id : undefined}
          onCreated={() => {
            setEdit(null);
            setNote(edit === "resubmit" ? "원고를 다시 보냈습니다. 운영팀이 다시 확인합니다." : "새 발행을 요청했습니다.");
          }}
        />
      )}
    </Dialog>
  );
}

/** 원고 머리말의 공개 범위 (서버 파서와 같은 규칙의 간단판: 승인 패널의 키워드 입력 여부에만 쓴다). */
function manuscriptVisibility(md: string | null | undefined): string | null {
  if (!md) return null;
  const head = /^\s*---\r?\n([\s\S]*?)\r?\n---/.exec(md);
  const line = head?.[1].split(/\r?\n/).find((l) => /^\s*(공개|공개범위|visibility)\s*[:：]/.test(l));
  if (!line) return "public";
  const v = line.split(/[:：]/)[1]?.replace(/\s+/g, "").toLowerCase() ?? "";
  if (/^(이웃|이웃공개|neighbors)$/.test(v)) return "neighbors";
  if (/^(비공개|private)$/.test(v)) return "private";
  return "public";
}

export function TokenBadge({ n }: { n: number }) {
  return <Badge>{num(n)}</Badge>;
}

/** Job 이름이 비었거나 작업 유형 코드 그대로면 한글 이름으로 보여준다. */
/** 시스템 작업(스케줄러 예약 작업) 화면 이름 — server/apps/scheduler SYSTEM_JOB_SCHEDULES 와 같은 이름. */
export const systemJobNames: Record<string, string> = {
  subscription_expiry: "구독 만료 처리",
  held_release: "보류 해제",
  sanction_escalation: "제재 단계 올리기",
  cleanup: "오래된 기록 정리",
  seo_collect: "SEO 순위 수집",
  seo_trend: "검색 추이 수집",
  seo_keyword_tool: "키워드 검색 수 수집",
  llm_price_sync: "AI 모델 단가 갱신",
};

export function jobTitle(name: string | null | undefined): string {
  if (!name) return "이름 없는 작업";
  return taskTypeNames[name] ?? systemJobNames[name] ?? name;
}
